import { createRequire } from 'node:module'
import { existsSync } from 'node:fs'
import { join, dirname } from 'node:path'
import { fileURLToPath } from 'node:url'

/**
 * node-pty must be loaded lazily with a fallback.
 *
 * npm 12+ (bundled with Node 24) blocks dependency install scripts by
 * default. node-pty's `install` script (node-gyp) is what compiles the
 * native addon on Linux — no prebuilt linux-x64 binary ships in the npm
 * package (only darwin/win32 prebuilds). So on Linux/WSL the npm-installed
 * node-pty is a dead module and `require('node-pty')` throws at load time.
 *
 * The CLI tarball ships a prebuilt linux-x64 node-pty under
 * `vendor/node-pty/` (compiled against N-API, so it's ABI-stable across
 * Node versions). We try the real dependency first and fall back to the
 * vendored copy so candidates on Linux/WSL aren't broken by npm 12.
 */
type PtyModule = typeof import('node-pty')

let cached: PtyModule | null = null

/** Best-effort npm version, e.g. "12.1.0" — from the npm user-agent. */
function npmVersion(): string {
  const ua = process.env['npm_config_user_agent'] || ''
  const match = ua.match(/npm\/(\d+\.\d+\.\d+)/)
  return match ? match[1]! : 'unknown'
}

function loadPty(): PtyModule {
  if (cached) return cached

  const require = createRequire(import.meta.url)

  // 1. Try the real dependency (works on macOS/Windows via prebuilds, and
  //    on Linux when install scripts ran).
  let realError: unknown
  try {
    cached = require('node-pty') as PtyModule
    return cached
  } catch (err) {
    realError = err
    // Fall through to the vendored build.
  }

  // 2. Vendored builds shipped inside the CLI package.
  //    Locate them relative to this module. The CLI bundles @tonty/core, so
  //    import.meta.url points into the CLI's dist/: installed via npm the
  //    layout is <pkg>/dist → <pkg>/vendor (one level up). In the monorepo
  //    the built core sits at packages/core/dist → packages/cli/vendor.
  //    Try a few offsets to cover both. A platform-specific copy
  //    (node-pty-<platform>-<arch>, e.g. node-pty-linux-arm64) wins over
  //    the generic vendored build (linux-x64) when one is shipped.
  const here = dirname(fileURLToPath(import.meta.url))
  const ptyName = `node-pty-${process.platform}-${process.arch}`
  const parents = ['..', join('..', '..'), join('..', '..', '..'), join('..', '..', '..', '..')]
  const candidates: string[] = []
  for (const parent of parents) {
    candidates.push(join(here, parent, 'vendor', ptyName, 'lib', 'index.js'))
    candidates.push(join(here, parent, 'vendor', 'node-pty', 'lib', 'index.js'))
  }
  const tried: string[] = []
  for (const vendoredEntry of candidates) {
    tried.push(vendoredEntry)
    if (existsSync(vendoredEntry)) {
      cached = require(vendoredEntry) as PtyModule
      return cached
    }
  }

  // 3. Nothing worked — rethrow with full environment diagnostics so the
  //    candidate's exact setup can be pinpointed from the error text alone.
  const why = realError instanceof Error ? realError.message : String(realError)
  const knownIssue =
    process.platform === 'darwin' && process.arch === 'arm64'
      ? 'Known issue on macOS (Apple Silicon): the node-pty native addon\n' +
        'fails to load when npm 12 (bundled with Node 24) blocks install\n' +
        'scripts. The prebuilt darwin-arm64 fallback should cover this —\n' +
        'if you still see this error, reinstall once with:\n' +
        '  npm install -g --allow-scripts=node-pty tonty-tech-interviews\n' +
        'or switch to Node 20/22 and reinstall:\n' +
        '  nvm install 22 && nvm use 22 && npm i -g tonty-tech-interviews\n\n'
      : ''
  throw new Error(
    'node-pty failed to load and no vendored fallback was found.\n' +
      `  platform: ${process.platform} (${process.arch})\n` +
      `  node: ${process.version} (npm ${npmVersion()})\n` +
      `  real node-pty error: ${why}\n` +
      `  vendored candidates tried:\n    ${tried.join('\n    ')}\n\n` +
      knownIssue +
      'Most likely cause: npm 12 (bundled with Node 24) blocks install ' +
      'scripts, so the npm-installed node-pty has no compiled addon on ' +
      'this platform. Fixes:\n' +
      '  • npm >= 11.5: `npm install -g --allow-scripts=node-pty ' +
      'tonty-tech-interviews` (reinstall once so the native addon compiles)\n' +
      '  • or use Node 20/22 (npm 10/11), where install scripts run normally',
  )
}

/** Shared node-pty accessor — use instead of `import * as pty from 'node-pty'`. */
export function getPty(): PtyModule {
  return loadPty()
}
